import { DecimalPipe, NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import {
	GetSocialListeningMyBrandAnalyticsTopInfluencersByVoiceShareService,
	TopInfluencerByVoiceShare
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-top-influencers-by-voice-share.service';
import {
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TableWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import { applyContentItemOverrides } from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportBlockRender } from '../../../../services/report-blocks.service';
import {
	ReportSectionChartSwitch,
	sectionChartSource,
	sectionChartSwitch
} from '../../../../services/report-chart-types';
import { ReportChartSwitchComponent } from '../../components/report-chart-switch/report-chart-switch.component';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

// 7 rows — matches the Figma frame and keeps the leaderboard + distribution
// inside the fixed-height report slide (20 overflowed the card).
const MAX_INFLUENCERS = 7;

// Categorical palette — one color per influencer row, in order, mirroring the
// LOLLY report mockup (Figma 13302:100833). The leaderboard avatar chip and the
// matching distribution bar share the row color. Cycles if there are >10 rows.
const ROW_COLORS = [
	'#2872e1',
	'#e94f6a',
	'#2bb673',
	'#a98cf0',
	'#f5c451',
	'#6cd6c8',
	'#f0a3c6',
	'#5b8def',
	'#9aa7c7',
	'#cbb892'
];

// Human-readable channel labels for known platform keys; unknown keys are
// title-cased as a fallback.
const CHANNEL_LABELS: Record<string, string> = {
	youtube: 'YouTube',
	twitter: 'Twitter',
	x: 'Twitter',
	instagram: 'Instagram',
	tiktok: 'TikTok',
	facebook: 'Facebook',
	linkedin: 'LinkedIn',
	reddit: 'Reddit',
	news: 'News',
	web: 'Web',
	blog: 'Blog',
	newsletter: 'Newsletter',
	forum: 'Forum'
};

interface LeaderRow {
	id: string;
	rank: number;
	initial: string;
	handle: string;
	channel: string;
	sharePct: string;
	mentions: number;
	color: string;
	barWidthPct: number;
}

@Component({
	standalone: true,
	selector: 'app-top-influencers-by-voice-share-slide',
	templateUrl: './top-influencers-by-voice-share-slide.component.html',
	styleUrls: ['./top-influencers-by-voice-share-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		DecimalPipe,
		IconComponent,
		TypographyComponent,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportChartSwitchComponent,
		ReportSlideBrandComponent
	]
})
export class TopInfluencersByVoiceShareSlideComponent
	implements OnChanges, OnDestroy
{
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();
	/** The reader picked another type for this section's chart. */
	@Output() chartTypeChange = new EventEmitter<ReportBlockRender | null>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	dateLabel = '';
	summaryText = '';
	titleColor = '#2872e1';
	rows: LeaderRow[] = [];
	topShareValue = '';
	topShareLabel = 'Top-5 SoV share';
	/** What the chart is drawn as: its own design until the reader switches. */
	chartSwitch: ReportSectionChartSwitch = sectionChartSwitch(
		'top_influencers_by_voice_share',
		null,
		null
	);

	get isEmpty(): boolean {
		return !this.rows.length;
	}

	private readonly destroy$ = new Subject<void>();
	private cachedInfluencers: TopInfluencerByVoiceShare[] | null = null;
	private cacheKey: string | null = null;

	constructor(
		private readonly analyticsService: GetSocialListeningMyBrandAnalyticsTopInfluencersByVoiceShareService
	) {}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedInfluencers && this.cacheKey === currentCacheKey) {
			this.applyInfluencers(this.cachedInfluencers);
			return;
		}

		this.analyticsService
			.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedInfluencers = data;
				this.cacheKey = currentCacheKey;
				this.applyInfluencers(data);
			});
	}

	private applyInfluencers(data: TopInfluencerByVoiceShare[]): void {
		const sorted = [...data]
			.sort((a, b) => b.voiceShare - a.voiceShare)
			.slice(0, MAX_INFLUENCERS);
		const rawSlide = this.buildSlide(sorted);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide, sorted);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(influencers: TopInfluencerByVoiceShare[]): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;
		return {
			id: `${prefix}_slide`,
			slideType: 'top_influencers_by_voice_share',
			title: this.item.title || 'Top Influencers by Voice Share',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text:
							this.item.title || 'Top Influencers by Voice Share',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				{
					id: `${prefix}_table`,
					kind: 'table',
					visible: true,
					content: {
						dataSourceKey: 'top_influencers_by_voice_share_table',
						accentColor: '#2872e1',
						columns: [
							{ key: 'handle', label: 'Influencer' },
							{ key: 'channel', label: 'Channel' },
							{ key: 'share', label: 'Share' },
							{ key: 'mentions', label: 'Mentions' }
						],
						rows: influencers.map(i => ({
							handle: i.handle,
							channel: this.channelLabel(i.platform),
							share: `${i.voiceShare.toFixed(1)}%`,
							mentions: String(i.mentions)
						}))
					}
				}
			]
		};
	}

	private updateViewModel(
		slide: PreviewSlide,
		influencers: TopInfluencerByVoiceShare[]
	): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;
		const tableWidget = slide.widgets.find(
			widget => widget.kind === 'table' && !hidden.has(widget.id)
		) as TableWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';

		this.buildRows(tableWidget ? influencers : []);
	}

	private buildRows(influencers: TopInfluencerByVoiceShare[]): void {
		const maxShare = influencers.reduce(
			(m, i) => Math.max(m, i.voiceShare),
			0
		);

		this.rows = influencers.map((i, index) => ({
			id: `${i.handle}_${index}`,
			rank: index + 1,
			initial: this.initialOf(i.handle, i.name),
			handle: i.handle,
			channel: this.channelLabel(i.platform),
			sharePct: `${i.voiceShare.toFixed(1)}%`,
			mentions: i.mentions,
			color: ROW_COLORS[index % ROW_COLORS.length],
			// TRAP: 85, not 100 — the value label follows the longest bar and needs
			// room inside the slide (top-sites-by-visits-slide does the same).
			barWidthPct: maxShare > 0 ? (i.voiceShare / maxShare) * 85 : 0
		}));

		// KPI callout — combined share-of-voice of the top-5 influencers.
		const top5 = influencers.slice(0, 5);
		const sum = top5.reduce((s, i) => s + i.voiceShare, 0);
		this.topShareValue = top5.length ? `${sum.toFixed(1)}%` : '';

		// The distribution beside the leaderboard: each account's share of
		// voice, in the colour its avatar and its bar have here.
		this.chartSwitch = sectionChartSwitch(
			this.item.id,
			this.item.chartType,
			sectionChartSource(this.item.id, {
				labels: this.rows.map(row => row.handle),
				series: [
					{
						key: 'voice_share',
						label: 'Voice share',
						values: influencers.map(i => i.voiceShare),
						unit: '%'
					}
				],
				labelColors: this.rows.map(row => row.color),
				labelColumn: 'Influencer'
			})
		);
	}

	private channelLabel(platform: string): string {
		const key = (platform ?? '').toLowerCase();
		if (CHANNEL_LABELS[key]) return CHANNEL_LABELS[key];
		if (!key) return '—';
		return key.charAt(0).toUpperCase() + key.slice(1);
	}

	private initialOf(handle: string, name: string): string {
		const source = (handle || name || '').replace(/^@/, '').trim();
		const match = source.match(/[a-z0-9]/i);
		return match ? match[0].toUpperCase() : '—';
	}

	trackByRow(_: number, row: LeaderRow): string {
		return row.id;
	}
}
